import { z } from 'zod';
import { taskTypeZod } from 'packages/lib/api/annotations/contract/enums';

/**
 * Every `groupType` the analytics group endpoints accept. All Pipelines and All
 * Requests are the two scopes that do not identify a single group.
 */
export const groupTypeZod = z.enum([
  'requestID',
  'allrequests',
  'allpipelines',
  'pipeline',
  'workflow',
  'sampleGroup',
  'taskGroup',
]);
export type GroupType = z.infer<typeof groupTypeZod>;

/** One group, with enough detail to name it and link to its analytics page. */
export const groupRefZod = z.object({
  groupType: groupTypeZod,
  token: z.string(),
  name: z.string(),
});
export type GroupRef = z.infer<typeof groupRefZod>;

/**
 * The chain from the top-level pipeline down to and including the requested group,
 * e.g. `[pipeline, workflow, sampleGroup]` when a sample group was requested.
 */
export const groupHierarchyResponseZod = z.object({
  data: z.array(groupRefZod),
});
export type GroupHierarchyResponse = z.infer<typeof groupHierarchyResponseZod>;

/** Response from `GET /group/tasktypes`. */
export const groupTaskTypesResponseZod = z.array(taskTypeZod);
export type GroupTaskTypesResponse = z.infer<typeof groupTaskTypesResponseZod>;

/** Landing pages that aggregate every group of that kind and therefore have no token. */
export const isTokenlessGroupType = (
  groupType: GroupType
): groupType is 'allpipelines' | 'allrequests' =>
  groupType === 'allpipelines' || groupType === 'allrequests';

/**
 * Shared `groupType` / `groupToken` query shape. `groupToken` is omitted on All
 * Pipelines and All Requests; required everywhere else.
 */
export type GroupScopeQuery =
  | {
      groupType: 'allpipelines' | 'allrequests';
      groupToken?: string;
    }
  | {
      groupType: Exclude<GroupType, 'allpipelines' | 'allrequests'>;
      /** A group token matching `groupType`, or the request ID when `groupType` is `requestID`. */
      groupToken: string;
    };

export const toGroupScopeQuery = (
  groupType: GroupType,
  groupToken?: string
): GroupScopeQuery | null => {
  if (isTokenlessGroupType(groupType)) return { groupType, groupToken };
  return groupToken ? { groupType, groupToken } : null;
};

/** Query parameters for `GET /group/tasktypes`. */
export type GroupTaskTypesQuery = GroupScopeQuery;
